import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useTheme } from "@mui/material/styles";
import DateRangeCalendar from "screens/Customers/component/DateRangeCalendar";
import CustomLabel from "components/CustomLabel";
import dayjs from "dayjs";

const TimeClockReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [overtimeChecked, setOvertimeChecked] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("dayLevel");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = (option) => {
    setAnchorEl(null);
    if (option) console.log("Export as:", option);
  };

  const formatDateSubtractDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  };

  const [dateRange, setDateRange] = useState([
    dayjs(formatDateSubtractDays(new Date(), 90)),
    dayjs(formatDateSubtractDays(new Date(), 0)),
  ]);

  // Employee and Role Options
  const employeeOptions = [
    { value: "brooklyn", label: "Brooklyn Simmons" },
    { value: "cody", label: "Cody Fisher" },
    { value: "robert", label: "Robert Fox" },
    { value: "john", label: "John Doe" },
  ];

  const roleOptions = [
    { value: "Manager", label: "Manager" },
    { value: "Technician", label: "Technician" },
    { value: "Admin", label: "Admin" },
  ];

  // Filter employees based on search term (do not include 'all' here)
  const filteredEmployeeOptions = useMemo(() => {
    const filtered = !searchTerm
      ? employeeOptions
      : employeeOptions.filter((employee) =>
          employee.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    return filtered;
  }, [searchTerm, employeeOptions]);

  // Handle employee selection (supports 'all' toggle)
  const handleEmployeeChange = (event) => {
    const value = event.target.value;
    // value is an array because Select multiple is used
    if (Array.isArray(value) && value.includes("all")) {
      // toggle select all
      if (selectedEmployees.length === employeeOptions.length) {
        setSelectedEmployees([]);
      } else {
        setSelectedEmployees(employeeOptions.map((emp) => emp.value));
      }
    } else {
      setSelectedEmployees(value);
    }
  };

  // Handle role selection (supports 'all' toggle)
  const handleRoleChange = (event) => {
    const value = event.target.value;
    if (Array.isArray(value) && value.includes("all")) {
      if (selectedRoles.length === roleOptions.length) {
        setSelectedRoles([]);
      } else {
        setSelectedRoles(roleOptions.map((r) => r.value));
      }
    } else {
      setSelectedRoles(value);
    }
  };

  const handleOvertimeChange = (event) =>
    setOvertimeChecked(event.target.checked);
  const handleFilterChange = (event) => setSelectedFilter(event.target.value);
  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  // Display helpers
  const getSelectedEmployeeNames = () => {
    if (selectedEmployees.length === 0) return "Select Employee";
    if (selectedEmployees.length === employeeOptions.length)
      return "All Employees";

    const selectedNames = selectedEmployees.map((empValue) => {
      const employee = employeeOptions.find((opt) => opt.value === empValue);
      return employee ? employee.label : empValue;
    });

    return selectedNames.length <= 2
      ? selectedNames.join(", ")
      : `${selectedNames.slice(0, 2).join(", ")} +${
          selectedNames.length - 2
        } more`;
  };

  const getSelectedRoleNames = () => {
    if (selectedRoles.length === 0) return "Select Role";
    if (selectedRoles.length === roleOptions.length) return "All Roles";

    const selectedNames = selectedRoles.map((roleValue) => {
      const role = roleOptions.find((opt) => opt.value === roleValue);
      return role ? role.label : roleValue;
    });

    return selectedNames.length <= 2
      ? selectedNames.join(", ")
      : `${selectedNames.slice(0, 2).join(", ")} +${
          selectedNames.length - 2
        } more`;
  };

  const dayLevelRows = [
    {
      id: 1,
      employee: "Brooklyn Simmons",
      employeeValue: "brooklyn",
      role: "Manager",
      date: "09/18/2025",
      punchInTime: "08:00",
      punchOutTime: "17:30",
      breaks: "1:00",
      overtimeHours: "1:00",
      totalHoursExclOTBreak: "7:30",
      totalHours: "8:30",
    },
    {
      id: 2,
      employee: "Cody Fisher",
      employeeValue: "cody",
      role: "Technician",
      date: "08/15/2025",
      punchInTime: "09:00",
      punchOutTime: "18:15",
      breaks: "0:45",
      overtimeHours: "1:00",
      totalHoursExclOTBreak: "7:30",
      totalHours: "8:30",
    },
    {
      id: 3,
      employee: "Robert Fox",
      employeeValue: "robert",
      role: "Admin",
      date: "01/15/2025",
      punchInTime: "07:34",
      punchOutTime: "16:00",
      breaks: "0:30",
      overtimeHours: "0:00",
      totalHoursExclOTBreak: "7:45",
      totalHours: "7:45",
    },
    {
      id: 4,
      employee: "John Doe",
      employeeValue: "john",
      role: "Technician",
      date: "01/16/2025",
      punchInTime: "08:00",
      punchOutTime: "17:00",
      breaks: "1:00",
      overtimeHours: "0:00",
      totalHoursExclOTBreak: "8:00",
      totalHours: "8:00",
    },
  ];

  const aggregatedRows = [
    {
      id: 1,
      employee: "Brooklyn Simmons",
      employeeValue: "brooklyn",
      role: "Manager",
      totalHoursExclOTBreak: "120:30",
      totalBreak: "15:45",
      totalOvertimeHours: "12:30",
      totalHours: "117:15",
    },
    {
      id: 2,
      employee: "Cody Fisher",
      employeeValue: "cody",
      role: "Technician",
      totalHoursExclOTBreak: "95:15",
      totalBreak: "12:30",
      totalOvertimeHours: "8:45",
      totalHours: "91:30",
    },
    {
      id: 3,
      employee: "Robert Fox",
      employeeValue: "robert",
      role: "Admin",
      totalHoursExclOTBreak: "145:20",
      totalBreak: "18:15",
      totalOvertimeHours: "0:00",
      totalHours: "127:05",
    },
    {
      id: 4,
      employee: "John Doe",
      employeeValue: "john",
      role: "Technician",
      totalHoursExclOTBreak: "160:00",
      totalBreak: "20:00",
      totalOvertimeHours: "0:00",
      totalHours: "140:00",
    },
  ];

  const dayLevelColumns = [
    { field: "employee", headerName: "Employee Name", flex: 1, minWidth: 150 },
    { field: "role", headerName: "Employee Role", flex: 1, minWidth: 150 },
    { field: "date", headerName: "Date", flex: 1, minWidth: 100 },
    {
      field: "punchInTime",
      headerName: "Punch In Time",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "punchOutTime",
      headerName: "Punch Out Time",
      flex: 1,
      minWidth: 120,
    },
    { field: "breaks", headerName: "Breaks (HH:MM)", flex: 1, minWidth: 120 },
    {
      field: "overtimeHours",
      headerName: "Overtime Hours",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "totalHoursExclOTBreak",
      headerName: "Total Hours (Excl OT & Break)",
      flex: 1,
      minWidth: 180,
    },
    { field: "totalHours", headerName: "Total Hours", flex: 1, minWidth: 100 },
  ];

  const aggregatedColumns = [
    { field: "employee", headerName: "Employee Name", flex: 1, minWidth: 150 },
    { field: "role", headerName: "Employee Role", flex: 1, minWidth: 150 },
    {
      field: "totalHoursExclOTBreak",
      headerName: "Total Hours (Excl OT & Break)",
      flex: 1,
      minWidth: 180,
    },
    { field: "totalBreak", headerName: "Total Break", flex: 1, minWidth: 120 },
    {
      field: "totalOvertimeHours",
      headerName: "Total Overtime Hours",
      flex: 1,
      minWidth: 150,
    },
    { field: "totalHours", headerName: "Total Hours", flex: 1, minWidth: 100 },
  ];

  // Filter rows based on selected employees, roles, and overtime
  const filteredDayLevelRows = useMemo(() => {
    let filtered = dayLevelRows;
    if (selectedEmployees.length > 0)
      filtered = filtered.filter((row) =>
        selectedEmployees.includes(row.employeeValue)
      );
    if (selectedRoles.length > 0)
      filtered = filtered.filter((row) => selectedRoles.includes(row.role));
    if (overtimeChecked)
      filtered = filtered.filter((row) => {
        const [h, m] = row.overtimeHours.split(":").map(Number);
        return h * 60 + m > 0;
      });
    return filtered;
  }, [selectedEmployees, selectedRoles, overtimeChecked]);

  const filteredAggregatedRows = useMemo(() => {
    let filtered = aggregatedRows;
    if (selectedEmployees.length > 0)
      filtered = filtered.filter((row) =>
        selectedEmployees.includes(row.employeeValue)
      );
    if (selectedRoles.length > 0)
      filtered = filtered.filter((row) => selectedRoles.includes(row.role));
    if (overtimeChecked)
      filtered = filtered.filter((row) => {
        const [h, m] = row.totalOvertimeHours.split(":").map(Number);
        return h * 60 + m > 0;
      });
    return filtered;
  }, [selectedEmployees, selectedRoles, overtimeChecked]);

  const currentRows =
    selectedFilter === "dayLevel"
      ? filteredDayLevelRows
      : filteredAggregatedRows;
  const currentColumns =
    selectedFilter === "dayLevel" ? dayLevelColumns : aggregatedColumns;

  const handleNavigation = (to) => navigate(to);
  const handlePageBack = () => handleNavigation("/reports");

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Time Clock Report"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 3,
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4">Time Clock Report</Typography>
                <Typography variant="body1" color="text.secondary">
                  Employee timesheet with detailed clock-in/out times and
                  overtime
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="main"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
              >
                Export
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose(null)}
                sx={{ "& .MuiPaper-root": { minWidth: "110px" } }}
              >
                <MenuItem onClick={() => handleClose("csv")}>CSV</MenuItem>
                <MenuItem onClick={() => handleClose("xls")}>XLS</MenuItem>
                <MenuItem onClick={() => handleClose("pdf")}>PDF</MenuItem>
              </Menu>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC" }}
              >
                <Typography variant="h5">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
                <Grid container spacing={3}>
                  {/* Employee Name using LoginActivity-style Select + searchable menu item */}
                  {/* Employee Name */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ mb: 0, fontWeight: 500 }}>
                      Employee Name
                    </Typography>
                    <CustomTextField
                      select
                      fullWidth
                      size="small"
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected.length) {
                            return "Select Employee "; // ✅ acts as placeholder
                          }
                          return selected
                            .map(
                              (val) =>
                                employeeOptions.find((opt) => opt.value === val)
                                  ?.label || val
                            )
                            .join(", ");
                        },
                      }}
                      value={selectedEmployees}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value.includes("all")) {
                          if (
                            selectedEmployees.length === employeeOptions.length
                          ) {
                            setSelectedEmployees([]);
                          } else {
                            setSelectedEmployees(
                              employeeOptions.map((opt) => opt.value)
                            );
                          }
                        } else {
                          setSelectedEmployees(value);
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          height: "40px",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      {/* Search bar */}
                      <Box
                        sx={{
                          p: 1,
                          borderBottom: "1px solid #e0e0e0",
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#fff",
                          zIndex: 2,
                        }}
                      >
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="Search employees..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {/* Select All */}
                      <MenuItem value="all">
                        <em>Select All</em>
                      </MenuItem>

                      {/* Filtered employee list */}
                      {employeeOptions
                        .filter((opt) =>
                          opt.label
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                        )
                        .map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                    </CustomTextField>
                  </Grid>

                  {/* Employee Role */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ mb: 0, fontWeight: 500 }}>
                      Employee Role
                    </Typography>
                    <CustomTextField
                      select
                      fullWidth
                      size="small"
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected.length) {
                            return "Select Role"; // ✅ placeholder text
                          }
                          return selected
                            .map(
                              (val) =>
                                roleOptions.find((opt) => opt.value === val)
                                  ?.label || val
                            )
                            .join(", ");
                        },
                      }}
                      value={selectedRoles}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value.includes("all")) {
                          if (selectedRoles.length === roleOptions.length) {
                            setSelectedRoles([]);
                          } else {
                            setSelectedRoles(
                              roleOptions.map((opt) => opt.value)
                            );
                          }
                        } else {
                          setSelectedRoles(value);
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          height: "40px",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      <MenuItem value="all">
                        <em>Select All</em>
                      </MenuItem>
                      {roleOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>

                  {/* Date Range */}
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomLabel label="Date Range" />
                    <DateRangeCalendar
                      initialStartDate={dayjs}
                      initialEndDate={dayjs}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 2,
                      ml: 4,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={overtimeChecked}
                          onChange={handleOvertimeChange}
                          color="success"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Overtime
                        </Typography>
                      }
                    />
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={selectedFilter}
                        onChange={handleFilterChange}
                        row
                        sx={{ gap: 4 }}
                      >
                        <FormControlLabel
                          value="dayLevel"
                          control={<Radio color="success" size="small" />}
                          label={
                            <Typography variant="body2">
                              Day Level Report
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="aggregated"
                          control={<Radio color="success" size="small" />}
                          label={
                            <Typography variant="body2">
                              Aggregated Report
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  <Button variant="outlined" color="main" size="small">
                    Run Filter
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Data Grid */}
            <DataGridPro
              rows={currentRows}
              columns={currentColumns}
              // pageSize={5}
              // rowsPerPageOptions={[5, 10, 25]}
              autoHeight
              slots={{
                footer: () => null
              }}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F8FAFC",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default TimeClockReport;
